import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { useLocale } from "../lib/useLocale";
import api from "../lib/api";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
};

const MD = {
  h2: (p) => <h2 className="font-heading text-3xl md:text-4xl text-rr-ivory mt-12 mb-4" {...p} />,
  h3: (p) => <h3 className="font-heading text-2xl text-rr-ivory mt-8 mb-3" {...p} />,
  p: (p) => <p className="text-rr-ivory/75 text-lg leading-[1.85] mb-6" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 space-y-2 text-rr-ivory/75 text-lg mb-6" {...p} />,
  li: (p) => <li {...p} />,
  strong: (p) => <strong className="text-rr-ivory font-semibold" {...p} />,
  em: (p) => <em className="italic" {...p} />,
  blockquote: (p) => <blockquote className="border-l-2 border-rr-gold pl-5 my-8 font-heading text-2xl md:text-3xl italic text-rr-ivory/90 leading-snug" {...p} />,
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { path: lp } = useLocale();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-center" data-testid="blog-notfound">
        <h1 className="font-heading text-4xl text-rr-ivory">Article not found</h1>
        <Link to={lp("/blog")} className="btn-outline px-7 py-3 text-xs uppercase inline-flex items-center gap-2 mt-8">
          <ArrowLeft className="w-4 h-4" /> Back to the Journal
        </Link>
      </div>
    );
  }

  if (!post) return <div className="pt-40 pb-40 text-center text-rr-muted" data-testid="blog-loading">Loading…</div>;

  return (
    <div data-testid="blog-post-page">
      <Helmet>
        <title>{post.meta_title || `${post.title} | Rokert Journal`}</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        {post.cover_image && <meta property="og:image" content={post.cover_image} />}
      </Helmet>

      <article>
        <section className="relative h-[60vh] min-h-[420px] w-full overflow-hidden -mt-[74px] grain">
          <img src={post.cover_image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="relative z-10 h-full max-w-3xl mx-auto px-6 flex flex-col justify-end pb-14">
            <Link to={lp("/blog")} data-testid="blog-back" className="inline-flex items-center gap-2 text-rr-ivory/70 hover:text-rr-gold text-xs uppercase tracking-wider mb-6 w-fit">
              <ArrowLeft className="w-4 h-4" /> The Journal
            </Link>
            <div className="flex items-center gap-3 text-rr-ivory/60 text-xs uppercase tracking-wider">
              <span>{post.author}</span><span>·</span><span>{fmtDate(post.created_at)}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {post.read_minutes} min</span>
            </div>
            <h1 className="font-heading text-4xl md:text-6xl text-rr-ivory mt-4 leading-[1.02]">{post.title}</h1>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-20">
          <p className="text-rr-ivory/80 text-xl md:text-2xl font-heading leading-relaxed mb-10 pb-10 border-b border-rr-line">{post.excerpt}</p>
          <div data-testid="blog-body">
            <ReactMarkdown components={MD}>{post.body}</ReactMarkdown>
          </div>
          {Array.isArray(post.tags) && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-rr-line">
              {post.tags.map((tg) => (
                <span key={tg} className="text-xs text-rr-ivory/60 border border-rr-line rounded-full px-3.5 py-1.5">{tg}</span>
              ))}
            </div>
          )}
        </section>

        <section className="max-w-3xl mx-auto px-6 pb-28 text-center">
          <Link to={lp("/retreats")} data-testid="blog-cta" className="btn-gold px-9 py-4 inline-flex items-center gap-2 text-xs uppercase">
            Design Your Journey <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
          </Link>
        </section>
      </article>
    </div>
  );
}
